import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import FieldSet from 'flarum/common/components/FieldSet';
import Button from 'flarum/common/components/Button';
import Alert from 'flarum/common/components/Alert';
import SignUpModal from 'flarum/forum/components/SignUpModal';
import LogInModal from 'flarum/forum/components/LogInModal';

// 🚨 `m` is the Mithril GLOBAL that Flarum already provides. Importing
// 'mithril' here does not resolve — it is not a dependency of this package —
// and adding it as one would bundle a second copy of Mithril alongside the
// one the forum is already running.
/* global m */

const T = 'ernestdefoe-wowcraft-minecraft.forum.settings';

/**
 * 🚨 SettingsPage is extended BY PATH, not by importing it.
 *
 * Flarum 2 code-splits its pages: the module is not in the main bundle and is
 * fetched only when somebody opens that page. Importing it and reaching for
 * `.prototype` at initializer time therefore reads a property of undefined —
 * the extension dies before it registers anything, and because initializers
 * share one bundle, EVERY extension after it in the chain dies too. That is
 * how this broke Scribe's toolbar: Scribe was fine, it just never got to run.
 *
 * `extend` accepts a module path for precisely this case and defers until the
 * page is actually loaded.
 */
const SETTINGS_PAGE = 'flarum/forum/components/SettingsPage';

/**
 * 🚨 `/register` and `/login` are NOT pages. They answer 405 to a GET.
 *
 * Signing up in Flarum is a MODAL opened by JavaScript, so any plain
 * `<a href="/register">` — on the homepage, in a post, in a widget — is a dead
 * link that navigates to a Method Not Allowed. Page Builder's html block
 * strips event handlers (correctly), so the markup cannot open the modal
 * itself. One delegated listener makes every such link mean what it says.
 *
 * Delegated on `document`, so it also covers links that appear later: a
 * homepage block re-rendered by Mithril, a post loaded on scroll.
 */
function upgradeAuthLinks() {
  document.addEventListener('click', e => {
    const link = e.target.closest?.('a[href="/register"], a[href="/login"]');
    if (!link) return;

    // Let modified clicks behave normally — a middle-click or cmd-click that
    // silently opened a modal instead of a tab would be its own small bug.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const wantsSignUp = link.getAttribute('href') === '/register';

    /*
     * 🚨 Already signed in: send them where the call to action actually leads.
     *
     * "Take the road in" means get onto the server, and for somebody who has
     * already registered the remaining step is claiming their Minecraft name
     * or gamertag — which lives in settings. Dumping them on the forum index
     * instead reads as a broken button, because nothing happened that they
     * asked for.
     */
    if (app.session.user) {
      e.preventDefault();
      m.route.set(app.route('settings'));
      return;
    }
    e.preventDefault();

    /*
     * 🚨 Flarum 2 code-splits, so an imported component can still be undefined
     * at the moment this runs, and `app.modal.show(undefined)` throws inside a
     * click handler. Fall back to the header's OWN control, which is Flarum's
     * real wiring and cannot go stale, and to plain navigation after that. A
     * dead-link fix that can itself become a dead link is no fix.
     */
    /*
     * 🚨 Resolved from the REGISTRY at click time, not from the static import.
     *
     * Flarum 2 code-splits these: the import at the top of this file is
     * `undefined` when the bundle is evaluated, and only becomes resolvable
     * once the chunk has loaded. Asking the registry at the moment of the
     * click gets the real component; the static import is kept only as the
     * first thing tried in case a future build inlines it.
     */
    let Modal = wantsSignUp ? SignUpModal : LogInModal;
    if (!Modal) {
      try {
        Modal = flarum.reg.get('core', wantsSignUp ? 'forum/components/SignUpModal' : 'forum/components/LogInModal');
      } catch (err) {
        Modal = null;
      }
    }
    if (Modal && app.modal?.show) {
      try {
        app.modal.show(Modal);
        return;
      } catch (err) {
        // fall through to the header control
      }
    }
    const header = document.querySelector(wantsSignUp ? '.item-signUp button, .Button--signUp, .Header-secondary .item-signUp button' : '.item-logIn button, .Button--logIn, .Header-secondary .item-logIn button');
    if (header) {
      header.click();
      return;
    }
    m.route.set(app.route('index'));
  });
}
app.initializers.add('ernestdefoe/wowcraft-minecraft', () => {
  upgradeAuthLinks();
  extend(SETTINGS_PAGE, 'oninit', function () {
    this.mcName = app.session.user?.attribute('minecraftName') || '';
    this.mcSaving = false;
    this.bedrockTag = app.session.user?.attribute('bedrockGamertag') || '';
    this.bedrockSaving = false;
    this.hytaleCode = '';
    this.hytaleSaving = false;
  });
  extend(SETTINGS_PAGE, 'settingsItems', function (items) {
    items.add('wowcraft-minecraft', FieldSet.component({
      label: app.translator.trans(`${T}.heading`),
      className: 'Settings-mc'
    }, [m('p.helpText', app.translator.trans(`${T}.explain`)),
    // Shown only when the syncer has actually failed to resolve the
    // name. A warning that is always on screen is one nobody reads on
    // the day it matters.
    app.session.user?.attribute('minecraftResolvable') === false ? Alert.component({
      type: 'error',
      dismissible: false
    }, app.translator.trans(`${T}.unresolvable`)) : null, m('label', app.translator.trans(`${T}.label`)), m('input.FormControl', {
      type: 'text',
      value: this.mcName,
      maxlength: 16,
      placeholder: 'Steve',
      oninput: e => {
        this.mcName = e.target.value;
      }
    }), Button.component({
      className: 'Button Button--primary',
      loading: this.mcSaving,
      onclick: () => saveClaim(this)
    }, app.translator.trans(`${T}.save`)),
    // ── Bedrock ──
    //
    // 🚨 A SEPARATE field, not a second use of the one above. The two
    // are different alphabets, different length limits and different
    // accounts entirely — a player may well have both, and quite
    // reasonably under unrelated names. One box that means "whichever
    // you have" cannot be whitelisted, because the server needs to know
    // which door it is opening.
    m('hr.Settings-mcRule'), m('label', app.translator.trans(`${T}.bedrockLabel`)), m('p.helpText', app.translator.trans(`${T}.bedrockExplain`)), app.session.user?.attribute('bedrockResolvable') === false ? Alert.component({
      type: 'error',
      dismissible: false
    }, app.translator.trans(`${T}.bedrockUnresolvable`)) : null, m('input.FormControl', {
      type: 'text',
      value: this.bedrockTag,
      maxlength: 15,
      placeholder: 'Your Xbox gamertag',
      oninput: e => {
        this.bedrockTag = e.target.value;
      }
    }), Button.component({
      className: 'Button Button--primary',
      loading: this.bedrockSaving,
      onclick: () => saveBedrockClaim(this)
    }, app.translator.trans(`${T}.save`))]), -10);

    // ── Hytale ──
    //
    // 🚨 No name box here, unlike the two above. A Hytale link is PROVEN: the
    // game server shows a code to whoever holds the account, and typing it
    // here is the proof. A name anybody could type proves nothing.
    const hytaleName = app.session.user?.attribute('hytaleName');
    items.add('wowcraft-hytale', FieldSet.component({
      label: app.translator.trans(`${T}.hytaleHeading`),
      className: 'Settings-mc Settings-hytale'
    }, hytaleName ? [m('p', app.translator.trans(`${T}.hytaleLinked`, {
      name: m('strong', hytaleName)
    })), Button.component({
      className: 'Button',
      loading: this.hytaleSaving,
      onclick: () => unlinkHytale(this)
    }, app.translator.trans(`${T}.hytaleUnlink`))] : [m('p.helpText', app.translator.trans(`${T}.hytaleExplain`)), m('label', app.translator.trans(`${T}.hytaleLabel`)), m('input.FormControl.Settings-hytaleCode', {
      type: 'text',
      value: this.hytaleCode,
      maxlength: 7,
      autocomplete: 'off',
      autocapitalize: 'characters',
      spellcheck: false,
      placeholder: 'ABC123',
      oninput: e => {
        this.hytaleCode = e.target.value;
      },
      onkeydown: e => {
        if (e.key === 'Enter') linkHytale(this);
      }
    }), Button.component({
      className: 'Button Button--primary',
      loading: this.hytaleSaving,
      disabled: !this.hytaleCode.trim(),
      onclick: () => linkHytale(this)
    }, app.translator.trans(`${T}.hytaleLink`))]), -11);
  });
});
const HYTALE_URL = () => `${app.forum.attribute('apiUrl')}/wowcraft/hytale/link`;

/**
 * The two refusals the server words as a type, not a sentence, get one here;
 * anything else (a wrong or expired code is a 422 with its own message) falls
 * through to Flarum's own alert (returning false asks for it).
 */
function hytaleError(error) {
  const key = {
    409: 'hytaleTaken',
    429: 'hytaleTooMany'
  }[error.status];
  if (!key) return false;
  app.alerts.show({
    type: 'error'
  }, app.translator.trans(`${T}.${key}`));
}
export function linkHytale(page) {
  const code = (page.hytaleCode || '').trim();
  if (!code || page.hytaleSaving) return;
  page.hytaleSaving = true;
  app.request({
    method: 'POST',
    url: HYTALE_URL(),
    body: {
      code
    },
    errorHandler: hytaleError
  }).then(res => {
    app.session.user.pushAttributes({
      hytaleName: res.data.hytaleName
    });
    page.hytaleCode = '';
    app.alerts.show({
      type: 'success'
    }, app.translator.trans(`${T}.hytaleLinkedAlert`));
  }).catch(() => {}).then(() => {
    page.hytaleSaving = false;
    m.redraw();
  });
}
export function unlinkHytale(page) {
  page.hytaleSaving = true;
  app.request({
    method: 'DELETE',
    url: HYTALE_URL()
  }).then(() => {
    app.session.user.pushAttributes({
      hytaleName: null
    });
    app.alerts.show({
      type: 'success'
    }, app.translator.trans(`${T}.hytaleUnlinkedAlert`));
  }).catch(() => {}).then(() => {
    page.hytaleSaving = false;
    m.redraw();
  });
}
export function saveClaim(page) {
  page.mcSaving = true;
  const name = (page.mcName || '').trim();
  app.request({
    method: 'POST',
    url: `${app.forum.attribute('apiUrl')}/wowcraft/minecraft`,
    body: {
      data: {
        attributes: {
          mcName: name
        }
      }
    }
  }).then(res => {
    // Keep the session user in step so a redraw does not show the old value.
    app.session.user.pushAttributes({
      minecraftName: res.data.mcName,
      minecraftResolvable: res.data.resolvable !== false
    });
    app.alerts.show({
      type: 'success'
    }, app.translator.trans(name ? `${T}.saved` : `${T}.cleared`));
  }).catch(() => {}).then(() => {
    page.mcSaving = false;
    m.redraw();
  });
}
export function saveBedrockClaim(page) {
  page.bedrockSaving = true;
  const tag = (page.bedrockTag || '').trim();
  app.request({
    method: 'POST',
    url: `${app.forum.attribute('apiUrl')}/wowcraft/bedrock`,
    body: {
      data: {
        attributes: {
          gamertag: tag
        }
      }
    }
  }).then(res => {
    app.session.user.pushAttributes({
      bedrockGamertag: res.data.gamertag,
      bedrockResolvable: res.data.resolvable !== false
    });
    app.alerts.show({
      type: 'success'
    }, app.translator.trans(tag ? `${T}.bedrockSaved` : `${T}.cleared`));
  }).catch(() => {}).then(() => {
    page.bedrockSaving = false;
    m.redraw();
  });
}